import { useCallback, useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, PackageCheck, Truck } from "lucide-react";
import { CHALLAN_STATUS_LABELS, fmtDay, qty, type ChallanDetail, type ChallanRecord, type WorkOrderRecord, type WorkOrderStageRecord } from "@/types/production";
import { pickedToItems, RollPicker, type PickedRoll } from "./RollPicker";
import { ReasonDialog } from "./StageDialogs";

const today = () => new Date().toISOString().slice(0, 10);

export const challanTone = (status: string, overdue?: boolean) =>
  overdue ? "bg-rose-500/10 text-rose-700 border-rose-300" : status === "RECEIVED" ? "bg-emerald-500/10 text-emerald-700 border-emerald-300" : status === "CANCELLED" ? "bg-muted text-muted-foreground border-border" : "bg-amber-500/10 text-amber-700 border-amber-300";

export function ChallanBadge({ status, overdue }: { status: string; overdue?: boolean }) {
  return <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${challanTone(status, overdue)}`}>{overdue ? "Overdue" : CHALLAN_STATUS_LABELS[status] || status}</span>;
}

/** Job-work challans of a work order: send rolls to the job worker of a running stage, record goods coming back */
export function JobWorkSection({ w, canUpdate, reloadKey, onChanged, sendStage, onSendClose }: { w: WorkOrderRecord; canUpdate: boolean; reloadKey: number; onChanged: () => void; sendStage: WorkOrderStageRecord | null; onSendClose: () => void }) {
  const { error } = useToast();
  const [rows, setRows] = useState<ChallanRecord[]>([]);
  const [receive, setReceive] = useState<ChallanDetail | null>(null);
  const [cancel, setCancel] = useState<ChallanRecord | null>(null);
  const [detail, setDetail] = useState<ChallanDetail | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(unwrapPage<ChallanRecord>(await api.get(`/api/ProductionStock/Challans?work_order_id=${w.id}&page_size=50`)).rows);
    } catch {
      setRows([]);
    }
  }, [w.id]);
  useEffect(() => {
    void load();
  }, [load, reloadKey]);
  const changed = () => {
    void load();
    onChanged();
  };
  const fetchDetail = async (c: ChallanRecord) => unwrapObject<ChallanDetail>(await api.get(`/api/ProductionStock/ChallanById/${c.id}`));
  const active = w.work_order_status === "PLANNED" || w.work_order_status === "IN_PROGRESS";

  if (!rows.length && !sendStage) return null;
  return (
    <div className="bg-card border rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b">
        <h3 className="text-sm font-bold">Job work challans</h3>
        <p className="text-[11px] text-muted-foreground">Material sent to outside job workers and the goods that came back</p>
      </div>
      <ul className="divide-y text-sm">
        {rows.map((c) => (
          <li key={c.id} className="px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
            <div className="lg:w-[28%]">
              <button type="button" className="font-mono font-semibold text-primary hover:underline" onClick={async () => setDetail(await fetchDetail(c).catch(() => null))}>{c.challan_number}</button>
              <div className="text-xs text-muted-foreground">{c.supplier?.name} · {fmtDay(c.challan_date)}</div>
            </div>
            <div className="text-xs lg:w-[30%]">
              Sent <b>{qty(c.sent_qty, c.unit)}</b> on {c.roll_count} roll{c.roll_count === 1 ? "" : "s"}
              <div className="text-muted-foreground">
                Back {qty(c.received_qty)}{Number(c.wastage_qty) > 0 ? ` · lost ${qty(c.wastage_qty)}` : ""}
                {c.expected_return && c.challan_status !== "RECEIVED" && c.challan_status !== "CANCELLED" ? ` · due ${fmtDay(c.expected_return)}` : ""}
              </div>
            </div>
            <div className="lg:w-[14%]"><ChallanBadge status={c.challan_status} overdue={c.overdue} /></div>
            <div className="flex gap-1.5 lg:ml-auto">
              {canUpdate && (c.challan_status === "SENT" || c.challan_status === "PARTIAL") && (
                <Button size="sm" className="h-8 text-xs gap-1" onClick={async () => { const d = await fetchDetail(c).catch(() => null); if (d) setReceive(d); else error("Could not open", "Please try again"); }}>
                  <PackageCheck className="size-3.5" /> Receive goods
                </Button>
              )}
              {canUpdate && c.challan_status === "SENT" && Number(c.received_qty) === 0 && (
                <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600" onClick={() => setCancel(c)}>Cancel</Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <SendDialog w={w} stage={active ? sendStage : null} onClose={onSendClose} onSaved={changed} />
      <ReceiveDialog challan={receive} onClose={() => setReceive(null)} onSaved={changed} />
      <ReasonDialog
        open={!!cancel}
        title={`Cancel ${cancel?.challan_number}`}
        description="The rolls go back into stock."
        confirmLabel="Cancel challan"
        destructive
        onClose={() => setCancel(null)}
        onConfirm={async (reason) => {
          await api.patch(`/api/ProductionStock/ChallanCancel/${cancel!.id}`, { reason });
          changed();
        }}
      />
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{detail.challan_number} <ChallanBadge status={detail.challan_status} overdue={detail.overdue} /></DialogTitle>
                <DialogDescription>{detail.supplier?.name} · {detail.stage_name}{detail.vehicle_no ? ` · ${detail.vehicle_no}` : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground mb-1">Rolls sent</p>
                  {detail.rolls.map((r) => (
                    <div key={r.id} className="flex justify-between text-xs py-0.5"><span>Roll {r.unit_no} · lot {r.lot_number}</span><span>{qty(r.quantity, r.unit)}</span></div>
                  ))}
                </div>
                <div>
                  <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground mb-1">Goods received</p>
                  {detail.receipts.length === 0 && <p className="text-xs text-muted-foreground">Nothing back yet</p>}
                  {detail.receipts.map((r) => (
                    <div key={r.id} className="flex justify-between text-xs py-0.5">
                      <span>{fmtDay(r.received_date)}{r.reference_no ? ` · ${r.reference_no}` : ""}{r.is_final ? " · final" : ""}</span>
                      <span>{qty(r.received_qty, r.unit)}{Number(r.wastage_qty) > 0 ? ` (lost ${qty(r.wastage_qty)})` : ""}</span>
                    </div>
                  ))}
                  {detail.shortfall != null && detail.shortfall !== 0 && <p className="text-xs text-rose-600 mt-1">Short by {qty(detail.shortfall, detail.unit)}</p>}
                </div>
                {detail.notes && <p className="text-xs text-muted-foreground">{detail.notes}</p>}
                {detail.cancelled_reason && <p className="text-xs text-rose-600">Cancelled: {detail.cancelled_reason}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SendDialog({ w, stage, onClose, onSaved }: { w: WorkOrderRecord; stage: WorkOrderStageRecord | null; onClose: () => void; onSaved: () => void }) {
  const { success, error } = useToast();
  const [picked, setPicked] = useState<PickedRoll[]>([]);
  const [f, setF] = useState({ challan_date: today(), expected_return: "", vehicle_no: "", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (stage) {
      setPicked([]);
      setF({ challan_date: today(), expected_return: stage.planned_end || "", vehicle_no: "", notes: "" });
    }
  }, [stage?.id]);
  const unit = picked[0]?.unit.unit;
  const submit = async () => {
    if (!stage) return;
    if (!picked.length) return error("Validation Error", "Choose the rolls to send");
    setSaving(true);
    try {
      await api.post(`/api/ProductionStock/ChallanCreate/${w.id}`, { stage_id: stage.id, challan_date: f.challan_date, expected_return: f.expected_return || null, vehicle_no: f.vehicle_no, notes: f.notes, items: pickedToItems(picked) });
      success("Challan made", "The rolls have left stock");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not send", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!stage} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="size-4" /> Send material for {stage?.process_name}</DialogTitle>
          <DialogDescription>To {stage?.supplier?.name || "the job worker"}. All rolls on one challan must be counted in the same unit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Challan date</Label><Input type="date" value={f.challan_date} onChange={(e) => setF({ ...f, challan_date: e.target.value })} className="h-10" /></div>
            <div className="space-y-1.5"><Label>Expected back</Label><Input type="date" value={f.expected_return} onChange={(e) => setF({ ...f, expected_return: e.target.value })} className="h-10" /></div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Vehicle no.</Label><Input value={f.vehicle_no} onChange={(e) => setF({ ...f, vehicle_no: e.target.value })} className="h-10" /></div>
          </div>
          <RollPicker picked={picked} onChange={setPicked} onlyUnit={unit} />
          <div className="space-y-1.5"><Label>Notes</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="h-10" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !picked.length}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ challan, onClose, onSaved }: { challan: ChallanDetail | null; onClose: () => void; onSaved: () => void }) {
  const { success, error } = useToast();
  const [f, setF] = useState({ received_date: today(), received_qty: "", unit: "", wastage_qty: "", reference_no: "", is_final: false, notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (challan) setF({ received_date: today(), received_qty: "", unit: challan.receipt_unit || challan.unit, wastage_qty: "", reference_no: "", is_final: false, notes: "" });
  }, [challan?.id]);
  const locked = !!challan?.receipts.length;
  const submit = async () => {
    if (!challan) return;
    const q = Number(f.received_qty);
    if (!(q > 0)) return error("Validation Error", "Enter the quantity that came back");
    setSaving(true);
    try {
      await api.post(`/api/ProductionStock/ChallanReceive/${challan.id}`, { received_date: f.received_date, received_qty: q, unit: f.unit || undefined, wastage_qty: f.wastage_qty ? Number(f.wastage_qty) : undefined, reference_no: f.reference_no, is_final: f.is_final, notes: f.notes });
      success("Goods received", f.is_final ? "The challan is closed" : "");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!challan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Receive goods · {challan?.challan_number}</DialogTitle>
          <DialogDescription>
            {challan?.supplier?.name} was sent {qty(challan?.sent_qty, challan?.unit)}; {qty(challan?.received_qty)} is back so far. Tick "last delivery" to close the challan: material that has not come back counts as used.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-2"><Label>Quantity received *</Label><Input inputMode="decimal" value={f.received_qty} onChange={(e) => setF({ ...f, received_qty: e.target.value })} className="h-10" autoFocus /></div>
            <div className="space-y-1.5"><Label>Unit</Label><Input value={f.unit} disabled={locked} onChange={(e) => setF({ ...f, unit: e.target.value })} className="h-10" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Lost / wasted</Label><Input inputMode="decimal" value={f.wastage_qty} onChange={(e) => setF({ ...f, wastage_qty: e.target.value })} className="h-10" /></div>
            <div className="space-y-1.5"><Label>Received on</Label><Input type="date" value={f.received_date} onChange={(e) => setF({ ...f, received_date: e.target.value })} className="h-10" /></div>
          </div>
          <div className="space-y-1.5"><Label>Job worker's challan / bill no.</Label><Input value={f.reference_no} onChange={(e) => setF({ ...f, reference_no: e.target.value })} className="h-10" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="size-4" checked={f.is_final} onChange={(e) => setF({ ...f, is_final: e.target.checked })} /> This is the last delivery</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
